import { useRef, useState } from 'react'

import useUpload from 'hooks/useUpload'
import { computeCalculated } from './calc'
import DateInput from './DateInput'
import styles from './styles.module.scss'

// Columna tipo 'file': array de URLs (S3), mismo shape que producía la v1. Hook de
// upload propio (no en Field) para no llamarlo en las columnas que no son archivo.
function FileField({ value, onChange, disabled }) {
  const upload = useUpload()
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const files = (Array.isArray(value) ? value : []).filter((f) => typeof f === 'string' && f)

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || [])
    if (!picked.length) return
    setUploading(true)
    try {
      const links = await upload(picked)
      // Dedup: S3 da keys únicas, pero un array migrado podría repetir — evita
      // keys de React duplicadas y que "quitar" borre todas las copias a la vez.
      if (links) onChange([...new Set([...files, ...links])])
    } catch (error) {
      console.error('[custom-forms] file upload failed', error)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className={styles.fileField}>
      {files.map((url) => (
        <div key={url} className={styles.fileRow}>
          <a href={url} target="_blank" rel="noopener noreferrer">{url.replace('https://', '')}</a>
          {!disabled && (
            <button type="button" className={styles.iconbtn} onClick={() => onChange(files.filter((f) => f !== url))} aria-label="Quitar archivo">✕</button>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <input ref={inputRef} type="file" multiple className="d-none" onChange={onPick} disabled={uploading} />
          <button type="button" className={`${styles.btn} ${styles.outline}`} disabled={uploading} onClick={() => inputRef.current?.click()}>
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
        </>
      )}
    </div>
  )
}

// Input de carga para una columna, según su tipo. `invalid` = error que bloquea el
// guardado (pregunta obligatoria sin completar); `invalidMsg` = el motivo. Un número
// fuera de rango NO es invalid: se muestra un aviso propio y se guarda igual.
// `disabled` = ventana de edición vencida (ver isEditable en CustomFormRecordEditor).
function Field({ column, value, onChange, columns, data, invalid, invalidMsg, fieldRef, disabled }) {
  const label = (
    <label className={styles.fieldLabel}>
      {column.label || column.id}
      {column.required && <span className={styles.req}>*</span>}
      {column.unit && <span className={styles.note}> ({column.unit})</span>}
    </label>
  )
  const hint = invalid && <div className="text-danger small mt-1">{invalidMsg || 'Este campo es obligatorio.'}</div>

  if (column.type === 'calculated') {
    const computed = computeCalculated(column, columns, data)
    return (
      <div className={styles.field}>
        {label}
        <div className={styles.calcCell} style={{ padding: '.4rem .6rem', borderRadius: 8, minHeight: 34 }}>
          {computed || <span className={styles.note}>—</span>}
        </div>
      </div>
    )
  }

  if (column.type === 'paragraph') {
    return (
      <div className={styles.field}>
        {label}
        <textarea ref={fieldRef} className={`form-control ${invalid ? 'is-invalid' : ''}`} rows={3} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        {hint}
      </div>
    )
  }

  if (column.type === 'number') {
    // Fuera de rango NO bloquea: se guarda igual, pero se destaca en rojo con aviso
    // (los valores anormales suelen ser los clínicamente relevantes).
    const num = value == null || value === '' ? null : Number(value)
    const hasMin = Number.isFinite(column.min)
    const hasMax = Number.isFinite(column.max)
    const outOfRange = num != null && Number.isFinite(num) && ((hasMin && num < column.min) || (hasMax && num > column.max))
    const rangeText = hasMin && hasMax ? `${column.min}–${column.max}` : hasMin ? `≥ ${column.min}` : hasMax ? `≤ ${column.max}` : ''
    return (
      <div className={styles.field}>
        {label}
        <input
          ref={fieldRef}
          type="number" className={`form-control ${invalid || outOfRange ? 'is-invalid' : ''}`} style={{ maxWidth: 180 }}
          min={column.min} max={column.max} value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
        {invalid
          ? hint
          : (outOfRange && <div className="text-danger small mt-1">⚠ Valor fuera del rango normal ({rangeText}).</div>)}
      </div>
    )
  }

  if (column.type === 'date') {
    return (
      <div className={styles.field}>
        {label}
        <DateInput style={{ maxWidth: 180 }} className={`form-control ${invalid ? 'is-invalid' : ''}`} value={value ?? ''} disabled={disabled} onChange={onChange} />
        {hint}
      </div>
    )
  }

  if (column.type === 'boolean') {
    return (
      <div className={styles.field}>
        {label}
        <div className={styles.choice}>
          {[{ l: 'Sí', v: true }, { l: 'No', v: false }].map(({ l, v }) => (
            <button key={l} type="button" disabled={disabled} className={`${styles.opt} ${value === v ? styles.optOn : ''}`} onClick={() => onChange(v)}>{l}</button>
          ))}
        </div>
        {hint}
      </div>
    )
  }

  if (column.type === 'single_choice' || column.type === 'multiple_choice') {
    const multi = column.type === 'multiple_choice'
    const options = column.options || []
    const selected = multi ? (Array.isArray(value) ? value : []) : value
    const isOn = (v) => (multi ? selected.includes(v) : selected === v)
    const toggle = (v) => {
      if (!multi) return onChange(v)
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v])
    }
    return (
      <div className={styles.field}>
        {label}
        <div className={styles.choice}>
          {options.map((opt) => {
            const val = opt.value ?? opt.label ?? opt
            const text = opt.label ?? opt
            return (
              <button key={val} type="button" disabled={disabled} className={`${styles.opt} ${isOn(val) ? styles.optOn : ''}`} onClick={() => toggle(val)}>{text}</button>
            )
          })}
        </div>
        {hint}
      </div>
    )
  }

  if (column.type === 'scale') {
    const min = Number.isFinite(column.min) ? column.min : 0
    const max = Number.isFinite(column.max) ? column.max : 10
    const current = value == null || value === '' ? min : Number(value)
    return (
      <div className={styles.field}>
        {label}
        <div className={styles.scaleRow}>
          <span className={styles.scaleBound}>{min}</span>
          <input
            type="range"
            className={styles.scaleSlider}
            min={min}
            max={max}
            step={1}
            value={Number.isFinite(current) ? current : min}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className={styles.scaleBound}>{max}</span>
          <span className={styles.scaleValue}>{Number.isFinite(current) ? current : '—'}</span>
        </div>
        {hint}
      </div>
    )
  }

  if (column.type === 'file') {
    return (
      <div className={styles.field}>
        {label}
        <FileField value={value} onChange={onChange} disabled={disabled} />
        {hint}
      </div>
    )
  }

  // text (default)
  return (
    <div className={styles.field}>
      {label}
      <input ref={fieldRef} type="text" className={`form-control ${invalid ? 'is-invalid' : ''}`} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      {hint}
    </div>
  )
}

export default Field
